/**
 * Serviço centralizado para operações de documento do CorelDRAW
 * @module DocumentService
 * 
 * ✅ ATUALIZADO: Migrado para sendActionGuarded (HMAC obrigatório)
 * Todas as actions agora usam validação HMAC conforme exigido pelo backend C#
 */

import { corelCommunication } from '../communication';
import { debugLog, sendToTerminal } from '../../terminalLogger';
import { actionDebugger } from './actionDebugger';
import type {
  CloseDocumentOptions,
  CloseDocumentResponse,
  CloseDocumentCallback
} from './document.types';

/**
 * Serviço centralizado para operações de documento
 */
export class DocumentService {
  /**
   * Fecha o documento ativo no CorelDRAW
   * 
   * @param options Opções de configuração
   * @param callback Callback opcional para processar resposta
   * @returns Promise com resposta
   */
  static async closeDocument(
    options?: CloseDocumentOptions,
    callback?: CloseDocumentCallback
  ): Promise<CloseDocumentResponse> {
    const waitForResponse = options?.waitForResponse ?? true;
    const timeoutMs = options?.timeoutMs ?? 5000;
    const useFallback = options?.useFallback ?? true;
    const fallbackDelayMs = options?.fallbackDelayMs ?? 900;

    sendToTerminal('🔵 [DOCUMENT] Iniciando closeDocument', {
      waitForResponse,
      timeoutMs,
      useFallback
    });

    if (!waitForResponse) {
      // Fire-and-forget mode
      try {
        corelCommunication.sendActionGuarded('fechar-documento', {});
        sendToTerminal('✅ [DOCUMENT] closeDocument enviado (fire-and-forget)');
        return { status: 'ok' };
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        sendToTerminal('❌ [DOCUMENT] Erro em closeDocument', { error: errorMsg });
        return { status: 'error', message: errorMsg };
      }
    }

    // Wait for response mode
    return new Promise((resolve) => {
      let alreadyResolved = false;

      const startTime = Date.now();
      const finalize = (response: CloseDocumentResponse) => {
        if (alreadyResolved) return;
        alreadyResolved = true;

        const elapsedMs = Date.now() - startTime;
        if (response.status === 'ok' && response.result?.sucesso) {
          sendToTerminal('✅ [DOCUMENT] closeDocument concluído', {
            status: response.status,
            elapsedMs
          });
        } else {
          sendToTerminal('❌ [DOCUMENT] closeDocument falhou', {
            status: response.status,
            message: response.message,
            elapsedMs
          });
        }

        if (callback) {
          try {
            callback(response);
          } catch (cbError) {
            const errorMsg = cbError instanceof Error ? cbError.message : String(cbError);
            sendToTerminal('❌ [DOCUMENT] Erro no callback de closeDocument', {
              error: errorMsg
            });
          }
        }

        resolve(response);
      };

      // Timeout principal
      const mainTimeout = setTimeout(() => {
        sendToTerminal('⏱️ [DOCUMENT] Timeout em closeDocument', {
          timeoutMs,
          elapsedMs: Date.now() - startTime
        });
        finalize({
          status: 'timeout',
          message: 'Timeout ao fechar documento'
        });
      }, timeoutMs);

      // Fallback timer (mais curto, garante resposta rápida)
      let fallbackTimer: NodeJS.Timeout | null = null;
      if (useFallback) {
        fallbackTimer = setTimeout(() => {
          sendToTerminal('⏱️ [DOCUMENT] Fallback timeout atingido', {
            fallbackDelayMs
          });
          finalize({
            status: 'ok',
            result: {
              sucesso: true,
              mensagem: 'Fallback timeout (assumido sucesso)'
            }
          });
        }, fallbackDelayMs);
      }

      try {
        const requestId = corelCommunication.sendActionGuarded(
          'fechar-documento',
          {},
          (response: any) => {
            clearTimeout(mainTimeout);
            if (fallbackTimer) clearTimeout(fallbackTimer);

            const status = response?.status;
            const result = response?.result;
            const sucesso = result?.sucesso ?? (status === 'ok');

            if (!sucesso && status !== 'timeout') {
              sendToTerminal('⚠️ [DOCUMENT] Erro ao fechar documento', {
                status,
                message: response?.message || result?.mensagem
              });
            }

            finalize({
              status: status as any,
              result: {
                sucesso,
                mensagem: response?.message || result?.mensagem,
                acao: result?.acao
              }
            });
          }
        );

        if (!requestId) {
          clearTimeout(mainTimeout);
          if (fallbackTimer) clearTimeout(fallbackTimer);
          sendToTerminal('❌ [DOCUMENT] Falha ao enviar comando fechar-documento');
          finalize({
            status: 'error',
            message: 'Falha ao enviar comando fechar-documento'
          });
        }
      } catch (error) {
        clearTimeout(mainTimeout);
        if (fallbackTimer) clearTimeout(fallbackTimer);
        const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
        sendToTerminal('❌ [DOCUMENT] Exceção em closeDocument', { error: errorMsg });
        finalize({
          status: 'error',
          message: errorMsg
        });
      }
    });
  }

  /**
   * Fecha documento de forma segura (com retry)
   * 
   * @param maxRetries Número máximo de tentativas (padrão: 1 - sem retry)
   * @returns Promise<boolean> - true se fechado com sucesso
   */
  static async closeDocumentSafe(maxRetries: number = 1): Promise<boolean> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await this.closeDocument({
          waitForResponse: true,
          timeoutMs: 5 * 60 * 1000, // 5 min
          useFallback: true,
          fallbackDelayMs: 900
        });

        if (result.status === 'ok' || result.status === 'timeout') {
          // Timeout também é considerado sucesso (fallback)
          return true;
        }

        if (attempt < maxRetries) {
          // Aguardar antes de retry
          await new Promise(r => setTimeout(r, 500));
        }
      } catch (error) {
        sendToTerminal('⚠️ [DOCUMENT] Falha ao fechar documento', {
          attempt,
          maxRetries,
          error: error instanceof Error ? error.message : String(error)
        });

        if (attempt >= maxRetries) {
          return false;
        }
      }
    }

    return false;
  }
}

/**
 * Função auxiliar para compatibilidade
 * @deprecated Use DocumentService.closeDocument() diretamente
 */
export const closeDocument = DocumentService.closeDocument;

